<script setup lang="ts">
import { CirclePlus, Edit } from "@element-plus/icons-vue";
import _ from "lodash";
import { onUnmounted, ref, watch } from "vue";

import { ListDefinition, ListVisibility } from "~/api";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";

import { useMyInfoStore } from "~/stores/MyInfoStore";

import { Slotted } from "~/types/Slotted";
import { listVisibilityDescriptions } from "~/utils/enumDescriptions";
import ListsEditor from "./ListsEditor.vue";

const props = defineProps<{
  formDefinitionGuid?: string;
  workflowDefinitionGuid?: string;
  readonly: boolean;
}>();

const emit = defineEmits(["updated"]);
const myInfoStore = useMyInfoStore();

const listData = ref<ListDefinition[]>([]);

const editingList = ref<ListDefinition>();
const editorOpen = ref(false);

const getAllListDefinitions = async () => {
  if (!props.formDefinitionGuid && !props.workflowDefinitionGuid) {
    return;
  }

  await apiClient.listDefinition
    .getAllListDefinitions(
      props.formDefinitionGuid,
      props.workflowDefinitionGuid,
      myInfoStore.currentSysOrgGuid,
    )
    .then((res) => {
      listData.value = res;
    })
    .catch((error) => {
      showError(error);
      return [];
    });
};

const createListDefinition = (updatedList: ListDefinition) => {
  listData.value.push(updatedList);
};

const updateListDefinition = (updatedList: ListDefinition) => {
  listData.value = listData.value.map((l) =>
    l.listDefinitionGuid === updatedList.listDefinitionGuid ? updatedList : l,
  );
};

const deleteListDefinition = (list: ListDefinition) => {
  listData.value = listData.value.filter(
    (l) => l.listDefinitionGuid !== list.listDefinitionGuid,
  );
};

const handleOpenEditor = (list?: ListDefinition) => {
  console.log("Opening list editor");
  editingList.value = _.cloneDeep(list);
  editorOpen.value = true;
};

const handleCloseListEditor = () => {
  editingList.value = {} as ListDefinition;
  editorOpen.value = false;
};

const canEditForm = (list: ListDefinition) => {
  if (list.visibility === ListVisibility.PUBLIC) {
    return myInfoStore.isSuperAdmin;
  }
  if (
    list.visibility === ListVisibility.ORG_WIDE ||
    list.visibility === ListVisibility.FORM_SPECIFIC ||
    list.visibility === ListVisibility.WORKFLOW_SPECIFIC
  ) {
    return myInfoStore.currentSysOrgGuid === list.sysOrgGuid;
  }
};

/**
 * This function is used to get the display label for the visibility of the list.
 * @param list The list to check.
 * @returns The human-readable label for the visibility.
 */
const getVisibility = (list: ListDefinition) => {
  return listVisibilityDescriptions[list.visibility].label;
};

watch(
  () => [props.formDefinitionGuid, props.workflowDefinitionGuid],
  async ([formDefGuid, workflowDefGuid]) => {
    if (formDefGuid || workflowDefGuid) {
      await getAllListDefinitions();
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  listData.value = [];
  handleCloseListEditor();
});
</script>

<template>
  <ListsEditor
    :key="editingList?.listDefinitionGuid ?? 0"
    v-model="editorOpen"
    :form-definition-guid="props.formDefinitionGuid"
    :workflow-definition-guid="props.workflowDefinitionGuid"
    :editing-list="editingList"
    @close-drawer="handleCloseListEditor()"
    @added="
      ($event: ListDefinition) => {
        createListDefinition($event);
      }
    "
    @updated="
      ($event: ListDefinition) => {
        updateListDefinition($event);
      }
    "
    @deleted="
      ($event: ListDefinition) => {
        deleteListDefinition($event);
      }
    "
  />
  <div class="ListDefinitionsTab">
    <!-- to create a new list -->
    <el-button
      :icon="CirclePlus"
      class="add-list-button"
      :disabled="props.readonly"
      @click="handleOpenEditor()"
    >
      New list
    </el-button>

    <!-- table of lists "associated" with this form/the current organization -->
    <el-table :data="listData" border class="list-table">
      <el-table-column prop="name" label="Name" width="180" :sortable="true" />
      <el-table-column
        prop="items"
        label="List Options"
        width="250"
        :sortable="true"
      >
        <template #default="{ row }: Slotted<ListDefinition>">
          <div class="options">
            <el-tag v-for="(item, index) in row.items" :key="index" class="tag">
              {{ item.label }} - {{ item.value }}
            </el-tag>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="" label="Visibility" width="180" :sortable="true">
        <template #default="{ row }: Slotted<ListDefinition>">
          <div class="visibility">
            <el-tag type="info" size="large">
              {{ getVisibility(row) }}
            </el-tag>
          </div>
        </template>
      </el-table-column>
      <el-table-column
        prop=""
        label="Special List"
        width="180"
        :sortable="true"
      >
        <template #default="{ row }: Slotted<ListDefinition>">
          <div class="special-list">
            <el-tag type="info" size="large">
              {{ row.specialList ? row.specialList : "-" }}
            </el-tag>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="" label="Edit">
        <template #default="{ row }: Slotted<ListDefinition>">
          <div class="actions">
            <el-button
              v-if="canEditForm(row)"
              :icon="Edit"
              circle
              :disabled="editorOpen || props.readonly"
              @click="handleOpenEditor(row)"
            >
            </el-button>

            <el-tooltip
              v-else
              :content="
                row.visibility === ListVisibility.PUBLIC
                  ? 'You do not have permission to edit this list'
                  : ''
              "
              placement="top"
            >
              <el-button :icon="Edit" circle disabled />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<style lang="less">
.ListDefinitionsTab {
  display: grid;
  justify-items: center;
  grid-template-rows: min-content auto;
  grid-gap: 1em;
  overflow-y: auto;
  height: 100%;

  .add-list-button {
    justify-self: end;
  }

  .el-table .editing {
    --el-table-tr-bg-color: var(--el-color-warning-light-8);
  }
  .list-table {
    width: fit-content;

    .options {
      display: grid;
      .tag {
        margin: 5px;
      }
    }

    .visibility,
    .special-list {
      text-align: center;
    }

    .actions {
      display: grid;
      justify-items: center;
    }
  }
}
</style>
